import { chain, find, map, max, min, pick, slice, zipWith } from 'lodash'
import { generateCrud } from './util/vuex_resource'

const pickFieldsFromResponse = function(givenSheet) {
  return pick(givenSheet, [
    "id",
    "name",
    "parents"
  ])
}

const SpreadsheetModel = {
  name: 'spreadsheets',

  relationships: [
    { relation: 'referencedBy', model: 'game', dependent: "nullify" },
    { relation: 'referencedBy', model: 'component', dependent: "nullify" },
  ],

  create(newSheet) {
    return {
      ...pickFieldsFromResponse(newSheet),
      refreshedAt: null, /* starts unrefreshed */
      worksheets: [/* empty til refreshed */],
    }
  },

  getters: {
    findSpreadsheetWorksheet: (state, getters) => (spreadsheetId, worksheetId) => {
      const worksheets = getters.spreadsheetWorksheets(spreadsheetId)
      return find(worksheets, { id: worksheetId })
    },

    worksheetValues: (state, getters, _, rootGetters) => (spreadsheetId, worksheetId) => {
      return rootGetters.sheetValues(spreadsheetId, worksheetId)
        || []
    },

    spreadsheetWorksheets: (_, getters) => (spreadsheetId) => {
      return getters.findSpreadsheet(spreadsheetId).worksheets
    },

    worksheetTitles: (state, getters) => spreadsheetId => {
      const
        worksheets = getters.spreadsheetWorksheets(spreadsheetId),
        worksheetTitles = map(worksheets, "title")

      return worksheetTitles
    },

    worksheetMagicProperties: (state, getters) => (spreadsheetId, worksheetId) => {

      const
        properties = getters.worksheetPropertyNames(spreadsheetId, worksheetId),
        magicProperties = chain(properties)
          .map((prop) => prop.split(':'))
          .filter(["length", 2])
          .map((parts) => {
            return {
              layerName: parts[0],
              layerAttributeName: parts[1]
            }
          }).value()

      return magicProperties
    },

    worksheetPropertyNames: (state, getters) => (spreadsheetId, worksheetId) => {
      if(!spreadsheetId || !worksheetId) { return [] }
      const values = getters.worksheetValues(spreadsheetId, worksheetId)

      // First row is the property names
      return values[0] || []
    },

    worksheetPropertyValues: (state, getters) => (spreadsheetId, worksheetId) => {
      if(!spreadsheetId || !worksheetId) { return [] }
      const values = getters.worksheetValues(spreadsheetId, worksheetId)

      // Every row after the first are the value rows
      if(values.length > 1) {
        return values.slice(1) || []
      }

      return []
    },

    worksheetItems: (state, getters) => (spreadsheetId, worksheetId, firstRow, lastRow) => {
      firstRow = firstRow || 0
      if(lastRow != 0){
        lastRow = lastRow || Infinity
      }

      const propertyNames = getters.worksheetPropertyNames(spreadsheetId, worksheetId),
        propertyValues = getters.worksheetPropertyValues(spreadsheetId, worksheetId)

      firstRow = max([0, firstRow])
      lastRow = min([propertyValues.length, lastRow+1])

      const slicedPropertyValues = slice(propertyValues, firstRow, lastRow)

      return map(slicedPropertyValues, (row) => {
        return zipWith(propertyNames, row, (key, value) => {
          return { key, value }
        })
      })
    },
  },

  mutations: { },

  actions: {
    refreshSheetRecord({ dispatch }, spreadsheetId) {
      return dispatch("googleGetRecord", spreadsheetId)

        // Found it, patch our record
        .then((sheetRecord) => {
          return dispatch("patchSpreadsheet", pickFieldsFromResponse(sheetRecord))
        })

        .then(() => {
          return dispatch("refreshSheetIndex", spreadsheetId)
        })

        // Didn't find it, destroy our record
        .catch({ message: `File not found: ${spreadsheetId}.`}, () => {
          return dispatch("destroySpreadsheet", { id: spreadsheetId })
        })
    },

    ensureSheetIndexed({ dispatch, getters, rootGetters }, spreadsheetId) {
      const sheet = getters.findSpreadsheet(spreadsheetId)
      // check the store cache
      if(rootGetters.allSpreadsheetsCached(sheet)){
        return
      } else {
        // check the idb cache
        return dispatch("loadIDBCache", sheet)
          .then((success) => {
            if(!success) {
              // fetch from web
              return dispatch("refreshSheetIndex", spreadsheetId)
            }
          })
      }
    },

    refreshSheetIndex({ dispatch }, spreadsheetId) {
      return dispatch("googleFetchSheetById", spreadsheetId)
        .then(({ worksheets }) => {
          // add the worksheet metadata to the sheet
          return dispatch("patchSpreadsheet", {
            id: spreadsheetId,
            worksheets: map(worksheets, (worksheet) => {
              return pick(worksheet, ["id", "title"])
            }),
            refreshedAt: Date.now()

          }).then(() => {
            // specially cache the heavy part
            return dispatch("cacheSheet", {
              spreadsheetId,
              worksheets: map(worksheets, (worksheet) => {
                return pick(worksheet, ["id", "values"])
              })
            })
          })
        })
    }
  }
}

const SpreadsheetModule = generateCrud(SpreadsheetModel)

export default SpreadsheetModule
